import { Dayjs } from 'dayjs'
import { useCalendarSettings } from './use-calendar-settings'
import { useCallback, useMemo } from 'react'
import { TIME_INTERVAL } from '../constants'
import { Schedule } from '$api-client/types.gen'
import { DateHelper } from '$utils/date'

export type Slot = {
  id: number
  start: Dayjs
  finish: Dayjs
  isAvailable: boolean
  groomerId?: string
}

export type ScheduledPerson = {
  id: string
  first_name: string
  last_name: string
  schedule: Schedule[]
}

export type useCalendarSlotsProps = {
  weekDays: Dayjs[]
  people: ScheduledPerson[]
}

export function useCalendarSlots ({ weekDays, people }: useCalendarSlotsProps) {
  const { getSlotWindow, getPersonWorkingWindow } = useCalendarSettings()

  // Memoize slot calculations for each day and person combination
  const slotCache = useMemo(() => {
    const cache = new Map<string, Slot[]>()

    weekDays.forEach((day, weekDayIndex) => {
      const serviceHours = getSlotWindow(people, day)
      const actualDayOfWeek = DateHelper.toMondayWeekDay(day.day())

      // Cache slots for day without person
      const dayKey = `${weekDayIndex}`
      if (!cache.has(dayKey)) {
        const personWorkingWindow = getPersonWorkingWindow(actualDayOfWeek, undefined, day)
        cache.set(dayKey, calculateCalendarSlots(day, serviceHours, personWorkingWindow))
      }

      // Cache slots for each person on this day
      people.forEach(person => {
        const personKey = `${weekDayIndex}-${person.id}`
        if (!cache.has(personKey)) {
          const personWorkingWindow = getPersonWorkingWindow(actualDayOfWeek, person, day)
          cache.set(personKey, calculateCalendarSlots(day, serviceHours, personWorkingWindow, person.id))
        }
      })
    })

    return cache
  }, [weekDays, people, getSlotWindow, getPersonWorkingWindow])

  const getSlots = useCallback((weekDay: number, personId?: string) => {
    // Map the requested weekDay to available weekDays indices
    // In day view (weekDays.length === 1), all requests map to index 0
    // In week view (weekDays.length === 7), use the requested index directly
    const mappedWeekDay = weekDays.length === 1 ? 0 : weekDay

    const key = personId ? `${mappedWeekDay}-${personId}` : `${mappedWeekDay}`
    return slotCache.get(key) || []
  }, [slotCache, weekDays.length])

  return {
    getSlots,
  }
}

export const calculateCalendarSlots = (
  day: Dayjs,
  serviceHours: {
    start: Dayjs,
    finish: Dayjs
  },
  personWorkingWindows: Array<{
    start: Dayjs,
    finish: Dayjs
  }>,
  groomerId?: string,
) => {
  const paddedStart = serviceHours.start.minute() === 0
    ? serviceHours.start.subtract(15, 'minute')
    : serviceHours.start.startOf('hour').subtract(15, 'minutes')

  const paddedFinish = serviceHours.finish.minute() === 0
    ? serviceHours.finish.add(15, 'minute')
    : serviceHours.finish.startOf('hour').add(15, 'minutes')

  const slotDate = day
    .set('hour', paddedStart.hour())
    .set('minute', paddedStart.minute())
    .set('second', paddedStart.second())
    .set('millisecond', paddedStart.millisecond())

  const slotCount = paddedFinish.diff(paddedStart, 'minute') / TIME_INTERVAL
  const slots: Slot[] = []

  for (let i = 0; i < slotCount; i++) {
    const start = slotDate.add(i * TIME_INTERVAL, 'minute')
    const finish = slotDate.add((i + 1) * TIME_INTERVAL, 'minute')

    slots.push({
      id: i,
      start,
      finish,
      isAvailable: personWorkingWindows.some(window =>
        (start.isAfter(window.start) || start.isSame(window.start)) &&
        (finish.isBefore(window.finish) || finish.isSame(window.finish)),
      ),
      groomerId,
    })
  }

  return slots
}
